import { useContext, useState, useId } from 'react';
import { UserContext } from 'contexts/user/UserContext';
import { Alert, Button, Form } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { getReportDiagnostics } from 'helpers/errorDiagnostics';
import { getReportPagePath } from 'helpers/shipCrewChat';

function browserFamily() {
  const ua = navigator.userAgent;
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Edg\//.test(ua)) return 'Edge';
  if (/Chrome\//.test(ua)) return 'Chrome';
  if (/Safari\//.test(ua)) return 'Safari';
  return 'Other';
}

export default function ErrorReportForm({ code, eventId, diagnostics = getReportDiagnostics(null), onUnavailable }: { onUnavailable?: () => void; code: string; eventId: string | null; diagnostics?: ReturnType<typeof getReportDiagnostics> }) {
  const intl = useIntl();
  const descriptionId = useId();
  const user = useContext(UserContext)?.currentUser;
  const label = (id: string) => intl.formatMessage({ id: `error.report-${id}` });
  const [preview, setPreview] = useState<null | { code: string; eventId: string | null; path: string; browser: string; occurredAt: string; release: string | null; operationName: string | null; httpStatus: number | null; details: string[] }>(null);
  const [description, setDescription] = useState('');
  const [retrySeconds, setRetrySeconds] = useState(300);
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'failed' | 'limited'>('idle');
  const open = () => {
    const release = import.meta.env.VITE_APP_VERSION;
    setPreview({ code, eventId: eventId || diagnostics.eventId, operationName: diagnostics.operationName, httpStatus: diagnostics.httpStatus, details: diagnostics.details, path: getReportPagePath(window.location.pathname), browser: browserFamily(), occurredAt: new Date().toISOString(), release: typeof release === 'string' && /^[a-zA-Z0-9._-]{1,80}$/.test(release) ? release : null });
  };
  const send = async () => {
    if (state !== 'idle' || !preview) return;
    setState('sending');
    try {
      const response = await fetch('/api/error-reports', {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...preview, description }), signal: AbortSignal.timeout(35000),
      });
      if (response.status === 429) {
        const retry = Number(response.headers?.get('Retry-After'));
        setRetrySeconds(Number.isFinite(retry) && retry > 0 ? Math.ceil(retry) : 300);
        setState('limited'); onUnavailable?.(); return;
      }
      if (!response.ok || (await response.json()).ok !== true) throw new Error('Report not confirmed');
      setState('sent');
    } catch { setState('failed'); onUnavailable?.(); }
  };
  if (!preview) return <Button className="mt-3" variant="success" onClick={open}>{label('open')}</Button>;
  return <div className="mt-3">
    {user?.isAdmin === true && <>
      <p>{label('preview')}</p>
      {user.username && <p>{intl.formatMessage({ id: 'error.report-user' }, { username: user.username })}</p>}
      <pre className="text-wrap">{[preview.code, preview.eventId, `${window.location.origin}${preview.path}`, preview.occurredAt, preview.browser, preview.release, preview.operationName, preview.httpStatus, ...preview.details].filter(Boolean).join('\n')}</pre>
    </>}
    <Form.Group controlId={descriptionId}>
      <Form.Label>{label('description')}</Form.Label>
      <Form.Control as="textarea" maxLength={1500} value={description} disabled={state !== 'idle'} onChange={event => setDescription(event.target.value)} />
      <Form.Text>{label('privacy')}</Form.Text>
    </Form.Group>
    {state === 'sent' ? <Alert variant="success">{label('sent')}</Alert>
      : state === 'limited' ? <Alert variant="info">{intl.formatMessage({ id: 'error.report-limited' }, { seconds: retrySeconds })}</Alert>
      : state === 'failed' ? <Alert variant="warning">{label('failed')}</Alert>
        : <Button onClick={send} disabled={state === 'sending'}>{label(state === 'sending' ? 'sending' : 'send')}</Button>}
  </div>;
}
